import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, Transition as _Transition, withCtx as _withCtx, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "f-check-list__header-left" }
const _hoisted_2 = {
  key: 0,
  class: "f-input__required"
}
const _hoisted_3 = {
  key: 0,
  class: "f-check-list__value"
}
const _hoisted_4 = { class: "f-check-list__main" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = ["title"]

import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue';
import type { ScItem } from '@/types/sitecore';
import {
  FormCheckListEmits,
  FormCheckListItemFields,
  FormCheckListProps,
  FormCheckListRef,
} from '@/types/form/form-check-list';
import type { FormElMsgData } from '@/types/form/others/form-el-msg';
import type { DynamicFormContext, DynamicFormFieldValidateResult } from '@/types/form/dynamic-form';
import utils from '@/utils';
import FormElMsg from '@/form/other/FormElMsg.vue';
import SiteRichText from '@/elements/SiteRichText.vue';
import Checkbox from '@/elements/Checkbox.vue';
import { transitions } from '@/utils/transitions';


export default /*@__PURE__*/_defineComponent({
  __name: 'FormCheckList',
  props: {
    item: {},
    onChange: { type: Function }
  },
  emits: ["change"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;
const emit = __emit;
const formContext = inject<DynamicFormContext>('formContext')!;
const fields = computed(() => props.item.fields);
const rootEl = ref<HTMLElement>();

/* ---------- state ---------- */
const visibleValues = ref<string[]>([]);
const formCheckedItems = computed(
  () => fields.value?.items?.filter((x) => x.fields?.checked.value) ?? []
);
const checkedItems = ref<ScItem<FormCheckListItemFields>[]>(formCheckedItems.value);
const msg = ref<FormElMsgData | undefined>(undefined);
const isRequired = ref(fields.value?.required.value ?? false);
const isRadio = ref(fields.value?.radio?.value ?? false);
const isDisabled = ref(false);
const isVisible = ref(true);
const showItems = ref<ScItem<FormCheckListItemFields>[]>(fields.value?.items ?? []);
const expanded = ref<boolean>(
  fields.value?.collapsible?.value ? (fields.value?.expanded?.value ?? false) : true
);

/* ---------- computed ---------- */
const rootClass = computed(() => [
  'f-check-list',
  {
    required: isRequired.value,
    disabled: isDisabled.value,
    radio: isRadio.value,
    collapsible: fields.value?.collapsible.value,
    expanded: expanded.value,
  },
]);

const isItemChecked = (item: ScItem<FormCheckListItemFields>) => {
  return checkedItems.value.some((x) => x.fields?.data.value === item.fields?.data.value);
};

/* ---------- methods ---------- */
const isAllOption = (item: ScItem<FormCheckListItemFields> | undefined) => {
  const text = item?.fields?.mandatoryText?.value || '';
  return /^all\b/i.test(text);
};

const toggleItemChecked = (clickedItem: ScItem<FormCheckListItemFields>) => {
  if (isDisabled.value || !clickedItem.fields) return;
  msg.value = undefined;
  let value: ScItem<FormCheckListItemFields>[];
  if (isRadio.value) {
    const checked = checkedItems.value.some((x) => x.id === clickedItem.id);
    if (checked) {
      value = [];
    } else {
      value = [clickedItem];
    }
  } else {
    const allItems = fields.value?.items ?? [];
    const allItem = allItems.find((x) => isAllOption(x));

    if (allItem && clickedItem.id === allItem.id) {
      // "All" option clicked: toggle all items
      const isAllChecked = checkedItems.value.some((x) => x.id === allItem.id);
      value = isAllChecked ? [] : [...allItems];
    } else {
      // Regular item: toggle it
      value = [...checkedItems.value];
      const index = value.findIndex((x) => x.id === clickedItem.id);
      if (index >= 0) value.splice(index, 1);
      else value.push(clickedItem);

      // Sync "All" option state
      if (allItem) {
        const subItems = allItems.filter((x) => !isAllOption(x));
        const allSubSelected = subItems.every((x) => value.some((v) => v.id === x.id));
        const allIndex = value.findIndex((x) => x.id === allItem.id);
        if (allSubSelected && allIndex < 0) {
          value.push(allItem);
        } else if (!allSubSelected && allIndex >= 0) {
          value.splice(allIndex, 1);
        }
      }
    }
  }
  emit(
    'change',
    'form-check-list',
    fields.value?.fieldName.value ?? '',
    value.map((i) => i.fields?.data.value || ''),
    value
  );
  checkedItems.value = value;
};

const toggleExpanded = () => {
  expanded.value = !expanded.value;
};
watch(
  () => formCheckedItems.value,
  () => {
    checkedItems.value = formCheckedItems.value;
  },
  {
    immediate: true,
  }
);
/* ---------- watchers ---------- */
watch(
  () => [fields.value, visibleValues.value],
  () => {
    if (
      !utils.obj.isNoE(visibleValues.value) &&
      !utils.obj.isNoE(fields.value) &&
      !utils.obj.isNoE(fields.value?.items)
    ) {
      const items =
        visibleValues.value.length > 0
          ? fields.value!.items.filter((x) =>
              visibleValues.value.some((y) => utils.string.eq(x.fields!.data.value, y))
            )
          : fields.value!.items;
      if (!utils.obj.isNoE(items)) {
        showItems.value = items!;
      }
    }
  },
  { immediate: true }
);

watch(
  () => [checkedItems.value, fields.value, visibleValues.value],
  () => {
    const itemsList =
      visibleValues.value.length > 0
        ? fields.value!.items.filter((x) =>
            visibleValues.value.some((y) => utils.string.eq(x.fields!.data.value, y))
          )
        : fields.value!.items;

    showItems.value = itemsList.map((i) => {
      if (i.fields?.checked && i.fields?.disabled) {
        if (
          checkedItems.value.some((c) =>
            utils.string.eq(i.fields?.data?.value, c.fields?.data?.value)
          ) &&
          i.fields?.checked
        ) {
          i.fields.checked.value = true;
        } else {
          i.fields.checked.value = false;
          i.fields.disabled.value = !!(
            fields.value?.maxChoice?.value &&
            fields.value.maxChoice.value > 0 &&
            checkedItems.value.length >= fields.value.maxChoice.value &&
            i.fields?.disabled
          );
        }
      }
      return i;
    });
  },
  { immediate: true, deep: true }
);

watch(
  () => fields.value?.radio?.value,
  (newVal) => {
    isRadio.value = newVal ?? false;
  }
);

/* ---------- expose ---------- */
const getRefs = (): FormCheckListRef => {
  return {
    el: rootEl.value || null,
    id: props.item.id,
    name: fields.value?.fieldName.value,
    type: 'form-check-list',
    visible: isVisible.value,
    setRequired(required: boolean) {
      isRequired.value = required;
    },
    setDisabled(disabled: boolean) {
      isDisabled.value = disabled;
    },
    setVisible(visible: boolean) {
      isVisible.value = visible;
    },
    updateVisibleValues(values: string[]) {
      if (!fields.value?.items) return;
      visibleValues.value = values;
    },
    updateValue(value?: string | string[]) {
      if (!fields.value?.items || !value) {
        checkedItems.value = [];
        return;
      }
      let checkedValue: string[] = [];
      if (Array.isArray(value)) {
        checkedValue = value;
      } else {
        checkedValue = value.split(';');
      }

      checkedItems.value = fields.value?.items.filter((x) =>
        checkedValue.includes(x.fields!.data.value)
      );
    },
    async validate() {
      const result: DynamicFormFieldValidateResult = {
        value:
          checkedItems.value.length > 0
            ? checkedItems.value.map((x) => x.fields?.data.value).join(';')
            : undefined,
        valid: true,
      };
      if (!fields.value) {
        result.valid = false;
        return result;
      }
      if (isRequired.value && checkedItems.value.length === 0) {
        const _msg: FormElMsgData = {
          type: 'error',
          message: utils.obj.isNoE(fields.value?.requiredMsg?.value)
            ? 'This field is required'
            : fields.value.requiredMsg.value,
        };
        result.valid = false;
        result.message = _msg.message;
        msg.value = _msg;
        return result;
      }
      // if (fields.value?.maxChoice?.value && fields.value.maxChoice.value > 0 && checkedItems.value.length > fields.value.maxChoice.value) {
      //   const _msg: FormElMsgData = {
      //     type: 'error',
      //     message: utils.obj.isNoE(fields.value?.maxChoiceError?.value) ? '' : fields.value.maxChoiceError.value.replace('##count##', `${fields.value.maxChoice.value}`)
      //   };
      //   result.valid = false;
      //   result.message = _msg.message;
      //   msg.value = _msg;
      //   return result;
      // }
      return result;
    },
  } as FormCheckListRef;
};
onMounted(() => {
  formContext.regFormEl(getRefs());
});

onUnmounted(() => {
  formContext.unRegFormEl(getRefs());
});
__expose(getRefs());

return (_ctx: any,_cache: any) => {
  const _component_JssRichText = _resolveComponent("JssRichText")!
  const _component_icon = _resolveComponent("icon")!

  return (fields.value && isVisible.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass(rootClass.value),
        ref_key: "rootEl",
        ref: rootEl
      }, [
        (fields.value.collapsible?.value || !_unref(utils).obj.isNoE(fields.value.label?.value))
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "f-check-list__header",
              onClick: toggleExpanded
            }, [
              _createElementVNode("div", _hoisted_1, [
                _createElementVNode("div", {
                  class: _normalizeClass(["f-check-list__placeholder", [
            'f-check-list__placeholder',
            { 'as-label': fields.value.collapsible?.value && !expanded.value && checkedItems.value.length > 0 },
          ]])
                }, [
                  _createVNode(_component_JssRichText, {
                    class: "f-check-list__label",
                    field: fields.value.label,
                    tag: "span"
                  }, null, 8, ["field"]),
                  (isRequired.value)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_2, "*"))
                    : _createCommentVNode("", true)
                ], 2),
                _createVNode(_Transition, {
                  onBeforeEnter: _unref(transitions).accordion.beforeEnter,
                  onEnter: _unref(transitions).accordion.enter,
                  onLeave: _unref(transitions).accordion.leave
                }, {
                  default: _withCtx(() => [
                    (!expanded.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(checkedItems.value.map((x) => x.fields?.mandatoryText.value).join(' ')), 1))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }, 8, ["onBeforeEnter", "onEnter", "onLeave"])
              ]),
              (fields.value.collapsible?.value)
                ? (_openBlock(), _createBlock(_component_icon, {
                    key: 0,
                    class: "f-check-list__expander",
                    name: "down"
                  }))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true),
        _createVNode(_Transition, {
          onBeforeEnter: _unref(transitions).accordion.beforeEnter,
          onEnter: _unref(transitions).accordion.enter,
          onLeave: _unref(transitions).accordion.leave
        }, {
          default: _withCtx(() => [
            _withDirectives(_createElementVNode("div", null, [
              _createElementVNode("div", _hoisted_4, [
                _createVNode(SiteRichText, {
                  field: fields.value.description,
                  class: "f-check-list__des"
                }, null, 8, ["field"]),
                _createElementVNode("div", {
                  class: _normalizeClass(['f-check-list__items', { isRadio: isRadio.value }])
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(showItems.value, (showItem, index) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: index,
                      class: _normalizeClass([
                'f-check-list__item',
                {
                  checked: isItemChecked(showItem),
                  disabled: showItem.fields?.disabled?.value,
                },
              ])
                    }, [
                      _createVNode(Checkbox, {
                        "is-radio": isRadio.value,
                        value: isItemChecked(showItem),
                        onClick: ($event: any) => (toggleItemChecked(showItem))
                      }, null, 8, ["is-radio", "value", "onClick"]),
                      _createElementVNode("div", {
                        class: "f-check-list__item-label",
                        onClick: ($event: any) => (toggleItemChecked(showItem))
                      }, [
                        _createTextVNode(_toDisplayString(showItem.fields?.mandatoryText?.value) + " ", 1),
                        (showItem.fields?.tooltip?.value)
                          ? (_openBlock(), _createElementBlock("span", {
                              key: 0,
                              title: showItem.fields.tooltip.value,
                              class: "e-site-icon size-xs use-px"
                            }, "?", 8, _hoisted_6))
                          : _createCommentVNode("", true)
                      ], 8, _hoisted_5)
                    ], 2))
                  }), 128))
                ], 2),
                _createVNode(FormElMsg, { msg: msg.value }, null, 8, ["msg"])
              ])
            ], 512), [
              [_vShow, expanded.value]
            ])
          ]),
          _: 1
        }, 8, ["onBeforeEnter", "onEnter", "onLeave"])
      ], 2))
    : _createCommentVNode("", true)
}
}

})